import React from "react";
import { getMembers, types } from "mobx-state-tree";
import { uniqueId } from "../../../utilities/js-utils";
import { boundingBoxSidesForPoints, normalizeRotation, rotateBoundingBox, rotatePoint, rotationPoint } from "../model/drawing-utils";
import ErrorIcon from "../../../assets/icons/error.svg";
export const ObjectTypeIconViewBox = "0 0 36 34";
/**
 * This creates the definition for a type field in MST.
 * The field is optional so it doesn't have to be specified when creating
 * an instance.
 *
 * @param typeName the type
 * @returns
 */
export function typeField(typeName) {
    return types.optional(types.literal(typeName), typeName);
}
export const DrawingObject = types.model("DrawingObject", {
    type: types.optional(types.string, () => { throw "Type must be overridden"; }),
    id: types.optional(types.identifier, () => uniqueId()),
    x: types.number,
    y: types.number,
    hFlip: types.optional(types.boolean, false),
    vFlip: types.optional(types.boolean, false),
    rotation: types.optional(types.number, 0),
    visible: true
})
    .volatile(self => ({
    dragX: undefined,
    dragY: undefined,
    animating: false
}))
    .views(self => ({
    get position() {
        var _a, _b;
        // Uses the volatile dragX and dragY while the object is being dragged
        return {
            x: (_a = self.dragX) !== null && _a !== void 0 ? _a : self.x,
            y: (_b = self.dragY) !== null && _b !== void 0 ? _b : self.y
        };
    },
    /** The bounding box of the object without considering rotation. */
    get unrotatedBoundingBox() {
        throw "Subclass needs to implement unrotatedBoundingBox";
    },
    /** The bounding box of the object without considering rotation or current drag. */
    get undraggedUnrotatedBoundingBox() {
        throw "Subclass needs to implement undraggedUnrotatedBoundingBox";
    },
    get label() {
        // Object types should implement this to return a user-friendly short label,
        // used in the show/sort panel.
        return "Unknown object";
    },
    get icon() {
        // Should be overridden by all subclasses
        return (React.createElement(ErrorIcon, { viewBox: ObjectTypeIconViewBox }));
    },
    get isRotatedToHorizontal() {
        return [90, 270].includes(normalizeRotation(self.rotation));
    }
}))
    .views(self => ({
    get ariaLabel() {
        return self.label;
    }
}))
    .actions(self => ({
    /** Update object's temporary size/position with new values, that have already been adjusted for rotation. */
    setUnrotatedDragBounds(bounds) {
        throw "Subclass needs to implement setUnrotatedDragBounds";
    },
    /** Set the object's "real" location & size to its drag location & size. */
    resizeObject() {
        throw "Subclass needs to implement resizeObject";
    },
    setRotation(rotation) {
        self.rotation = rotation;
    }
}))
    .views(self => ({
    /** The curent bounding box of the object, including rotation, drags, and flips. */
    get boundingBox() {
        return rotateBoundingBox(self.unrotatedBoundingBox, self.rotation);
    },
    /** The stored bounding box of the object, including rotation, but not any current drag.  */
    get undraggedBoundingBox() {
        return rotateBoundingBox(self.undraggedUnrotatedBoundingBox, self.rotation);
    },
    get supportsResize() {
        return true;
    },
    /**
     * Returns the translation and scaling information that should be applied
     * to the Transformable group element to account for the objects's position, rotation, and flip state.
     */
    get transform() {
        const { unrotatedBoundingBox, hFlip, vFlip, position, rotation } = self;
        const transform = {
            corner: { x: unrotatedBoundingBox.se.x, y: unrotatedBoundingBox.se.y },
            position: { x: position.x, y: position.y },
            center: { x: 0, y: 0 },
            scale: { x: hFlip ? -1 : 1, y: vFlip ? -1 : 1 },
            rotation
        };
        // The x,y "position" of an object is the zero point that we flip over.
        // But depending on the object type, this position can be anywhere in its bounding box.
        // So for the bounding box to stay the same, we need to move the object to account
        // for the position-to-center distance.
        // Center of the object relative to its "position" point.
        const center = {
            x: (unrotatedBoundingBox.nw.x + unrotatedBoundingBox.se.x) / 2 - position.x,
            y: (unrotatedBoundingBox.nw.y + unrotatedBoundingBox.se.y) / 2 - position.y
        };
        if (hFlip) {
            transform.center.x = center.x * 2;
        }
        if (vFlip) {
            transform.center.y = center.y * 2;
        }
        return transform;
    }
}))
    .views(self => ({
    inSelection(selectionBox) {
        const { nw, se } = self.boundingBox;
        return selectionBox.overlaps(nw, se);
    },
}))
    .actions(self => ({
    setVisible(visible) {
        self.visible = visible;
    },
    setAnimating(animating) {
        self.animating = animating;
    },
    setPosition(x, y) {
        self.x = x;
        self.y = y;
    },
    setDragPosition(x, y) {
        self.dragX = x;
        self.dragY = y;
    },
    repositionObject() {
        var _a, _b;
        self.x = (_a = self.dragX) !== null && _a !== void 0 ? _a : self.x;
        self.y = (_b = self.dragY) !== null && _b !== void 0 ? _b : self.y;
        self.dragX = self.dragY = undefined;
    },
    /** Make the object fit in the given bounding box. */
    setDragBoundsAbsolute(sides) {
        const nw = { x: sides.left, y: sides.top };
        const ne = { x: sides.right, y: sides.top };
        const sw = { x: sides.left, y: sides.bottom };
        const se = { x: sides.right, y: sides.bottom };
        // Center of rotation is the original se corner, but where that is depends on the rotation.
        const center = rotationPoint({ nw, se }, self.rotation);
        // Reverse-rotate each corner around the center of rotation
        // to get back to where it would be before rotation.
        const rotatedNW = rotatePoint(nw, center, -self.rotation);
        const rotatedNE = rotatePoint(ne, center, -self.rotation);
        const rotatedSE = rotatePoint(se, center, -self.rotation);
        const rotatedSW = rotatePoint(sw, center, -self.rotation);
        const trueBoundingBox = boundingBoxSidesForPoints([rotatedNW, rotatedNE, rotatedSE, rotatedSW]);
        self.setUnrotatedDragBounds(trueBoundingBox);
    },
}))
    .actions(self => ({
    /**
     * Temporarily adjust the edges of the object's bounding box by the given deltas.
     * This will change the size and origin position of the object, with changes stored as volatile fields.
     *
     * @param deltas
     */
    setDragBounds(deltas) {
        const currentBoundingBox = self.undraggedBoundingBox;
        const newSides = {
            top: currentBoundingBox.nw.y + deltas.top,
            bottom: currentBoundingBox.se.y + deltas.bottom,
            left: currentBoundingBox.nw.x + deltas.left,
            right: currentBoundingBox.se.x + deltas.right
        };
        self.setDragBoundsAbsolute(newSides);
    }
}));
/** Stroked objects have a stroke color, stroke width, and stroke dash array. */
export const StrokedObject = DrawingObject.named("StrokedObject")
    .props({
    stroke: types.string,
    strokeDashArray: types.string,
    strokeWidth: types.number
})
    .actions(self => ({
    setStroke(stroke) { self.stroke = stroke; },
    setStrokeDashArray(strokeDashArray) { self.strokeDashArray = strokeDashArray; },
    setStrokeWidth(strokeWidth) { self.strokeWidth = strokeWidth; }
}));
// There might be a better way to do this. It is currently just looking for a
// setStroke action. In a real type system
// like Java it'd be possible to identify if the object is an instanceof
// StrokedObject. There is an un-resolved MST issue about exposing the type
// hierarchy: https://github.com/mobxjs/mobx-state-tree/issues/1114
// Alternatively, I tried to add static volatile props like isStrokedObject to
// the drawing object itself. But there isn't a good way to start that property
// out as false, set it to true in the stroked object, and then pickup up this
// default in each of the instances of stroked object.
export function isStrokedObject(object) {
    return getMembers(object).actions.includes("setStroke");
}
/** Filled objects have a fill color. */
export const FilledObject = DrawingObject.named("FilledObject")
    .props({
    fill: types.string
})
    .actions(self => ({
    setFill(fill) { self.fill = fill; }
}));
export function isFilledObject(object) {
    return getMembers(object).actions.includes("setFill");
}
/** "Editable" objects go into an "editing" state if you click them while they are already selected.
 * For example, text labels go into a state where you can edit the text.
 */
export const EditableObject = DrawingObject.named("EditableObject")
    .volatile(self => ({
    isEditing: false
}))
    .actions(self => ({
    setEditing(editing) { self.isEditing = editing; }
}));
export function isEditableObject(object) {
    return getMembers(object).actions.includes("setEditing");
}
export const DeltaPoint = types.model("DeltaPoint", {
    dx: types.number, dy: types.number
});
export class DrawingTool {
    constructor(drawingLayer) {
        Object.defineProperty(this, "drawingLayer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.drawingLayer = drawingLayer;
    }
    handlePointerDown(e) {
        // handled in subclass
    }
    handleObjectClick(e, obj) {
        // handled in subclass
    }
}
export const computeStrokeDashArray = (type, strokeWidth) => {
    const dotted = isFinite(Number(strokeWidth)) ? Number(strokeWidth) : 0;
    const dashed = dotted * 3;
    switch (type) {
        case "dotted":
            return `${dotted},${dotted}`;
        case "dashed":
            return `${dashed},${dashed}`;
        default:
            return "";
    }
};
//# sourceMappingURL=data:application/json;base64,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